import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, X, Loader2, Users as UsersIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const ROLE_COLORS = {
  owner: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  editor: 'bg-green-100 text-green-800',
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'editor' });

  const loadData = () => {
    setLoading(true);
    adminAPI.getUsers().then(r => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ name: '', email: '', password: '', role: 'editor' });
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: '', role: u.role });
    setEditing(u.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.email.trim()) { toast.error('E-posta zorunlu'); return; }
    try {
      if (editing) {
        const data = { name: form.name, role: form.role };
        if (form.password) data.password = form.password;
        await adminAPI.updateUser(editing, data);
        toast.success('Kullanici guncellendi');
      } else {
        if (!form.password) { toast.error('Sifre zorunlu'); return; }
        await adminAPI.createUser(form);
        toast.success('Kullanici olusturuldu');
      }
      resetForm();
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kullanici silinsin mi?')) return;
    try { await adminAPI.deleteUser(id); toast.success('Silindi'); loadData(); }
    catch { toast.error('Silinemedi'); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Kullanicilar ve Roller</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" /> Kullanici Ekle
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editing ? 'Kullanici Duzenle' : 'Yeni Kullanici'}</CardTitle>
            <button onClick={resetForm}><X className="h-5 w-5" /></button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Ad</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="user-name-input" /></div>
              <div><Label>E-posta *</Label><Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} type="email" disabled={!!editing} data-testid="user-email-input" /></div>
              <div><Label>{editing ? 'Yeni Sifre (bos = degistirme)' : 'Sifre *'}</Label><Input value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} type="password" data-testid="user-password-input" /></div>
              <div>
                <Label>Rol</Label>
                <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} data-testid="user-submit">{editing ? 'Guncelle' : 'Olustur'}</Button>
              <Button variant="outline" onClick={resetForm}>Iptal</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid gap-3">
          {users.map(u => (
            <Card key={u.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <UsersIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-semibold">{u.name || u.email}</p>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                  </div>
                  <Badge variant="secondary" className={`${ROLE_COLORS[u.role] || ''} text-xs`}>{u.role}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(u)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
